<!-- ═══════════════════════════════════════════════════════════════════════
     En-tête + sélecteur de période
══════════════════════════════════════════════════════════════════════════ -->
<div class="mb-6">
  <div class="flex flex-wrap items-start justify-between gap-4 mb-3">
    <div class="flex items-center gap-3">
      <h2 class="text-2xl font-bold text-gray-900 tracking-tight">Dashboard</h2>
      @if (isLive) {
        <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 ring-1 ring-green-200 text-xs font-semibold text-green-700">
          <span class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>En direct
        </span>
      }
    </div>

    <div class="flex flex-wrap items-center gap-1.5">
      @for (p of presets; track p.value) {
        <button
          (click)="setPeriod(p.value)"
          class="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
          [class.bg-indigo-600]="selectedPreset === p.value"
          [class.text-white]="selectedPreset === p.value"
          [class.bg-white]="selectedPreset !== p.value"
          [class.text-gray-600]="selectedPreset !== p.value"
          [class.ring-1]="selectedPreset !== p.value"
          [class.ring-gray-200]="selectedPreset !== p.value"
        >{{ p.label }}</button>
      }
    </div>
  </div>

  @if (showDatePicker) {
    <div class="flex items-center gap-3 mt-2">
      <p-date-picker
        [(ngModel)]="customDates"
        selectionMode="range"
        [maxDate]="today"
        [showButtonBar]="true"
        [showIcon]="true"
        dateFormat="yy-mm-dd"
        placeholder="Début → Fin"
        (onSelect)="onCustomDatesSelect()"
      ></p-date-picker>
    </div>
  }
</div>

<!-- ═══════════════════════════════════════════════════════════════════════
     Cartes de statistiques (6 colonnes)
══════════════════════════════════════════════════════════════════════════ -->
<div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
  @for (card of [
    { key: 'visitors',      label: 'Visiteurs',     fmt: 'num' },
    { key: 'pageviews',     label: 'Pages vues',    fmt: 'num' },
    { key: 'viewsPerVisit', label: 'Vues / visite', fmt: 'dec' },
    { key: 'bounceRate',    label: 'Rebond',        fmt: 'pct' },
    { key: 'visitDuration', label: 'Durée moy.',    fmt: 'dur' }
  ]; track card.key) {
    <div class="bg-white rounded-xl p-4 ring-1 ring-gray-900/5 shadow-sm">
      <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 truncate">{{ card.label }}</p>
      @if (loading) {
        <div class="h-8 w-16 bg-gray-100 rounded animate-pulse"></div>
      } @else if (aggregate) {
        <p class="text-2xl font-bold text-gray-900">
          @if (card.fmt === 'num')  { {{ formatNumber($any(aggregate)[card.key]) }} }
          @if (card.fmt === 'dec')  { {{ ($any(aggregate)[card.key] ?? 0).toFixed(1) }} }
          @if (card.fmt === 'pct')  { {{ ($any(aggregate)[card.key] ?? 0) + '%' }} }
          @if (card.fmt === 'dur')  { {{ formatDuration($any(aggregate)[card.key] ?? 0) }} }
        </p>
      }
    </div>
  }

  <!-- En direct -->
  <div class="bg-white rounded-xl p-4 ring-1 ring-gray-900/5 shadow-sm relative overflow-hidden">
    @if (isLive) { <div class="absolute inset-0 bg-green-50/40"></div> }
    <p class="relative text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
      <span class="inline-block w-1.5 h-1.5 rounded-full"
        [class.bg-green-400]="isLive" [class.animate-pulse]="isLive"
        [class.bg-gray-300]="!isLive"></span>En direct
    </p>
    <p class="relative text-2xl font-bold text-gray-900">{{ realtime }}</p>
  </div>
</div>

<!-- ═══════════════════════════════════════════════════════════════════════
     Courbe trafic (pleine largeur)
══════════════════════════════════════════════════════════════════════════ -->
<div class="bg-white rounded-xl ring-1 ring-gray-900/5 shadow-sm p-5 mb-6">
  <h3 class="text-sm font-semibold text-gray-900 mb-4">Trafic dans le temps</h3>
  <div class="h-52">
    @if (timeseries.length > 0) {
      <p-chart type="line" [data]="timeseriesData" [options]="timeseriesOptions" height="208"></p-chart>
    } @else {
      <div class="flex items-center justify-center h-full text-sm text-gray-400">Aucune donnée</div>
    }
  </div>
</div>

<!-- ═══════════════════════════════════════════════════════════════════════
     Ligne 1 : Carte (gauche) + Appareils & Sources (droite)
══════════════════════════════════════════════════════════════════════════ -->
<div class="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">

  <!-- Carte Leaflet -->
  <div class="lg:col-span-3 bg-white rounded-xl ring-1 ring-gray-900/5 shadow-sm overflow-hidden flex flex-col">
    <div class="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
      <h3 class="text-sm font-semibold text-gray-900">Répartition géographique</h3>
      @if (countries.length > 0) {
        <span class="text-xs text-gray-400">{{ countries.length }} pays</span>
      }
    </div>
    <div class="flex-1" style="height:420px">
      <div #geoMapDiv style="width:100%;height:100%"></div>
    </div>
  </div>

  <!-- Appareils + Sources empilés -->
  <div class="lg:col-span-2 flex flex-col gap-4">

    <!-- Appareils -->
    <div class="bg-white rounded-xl ring-1 ring-gray-900/5 shadow-sm p-5 flex-1">
      <h3 class="text-sm font-semibold text-gray-900 mb-3">Appareils</h3>
      @if (devices.length > 0) {
        <div class="h-36">
          <p-chart type="doughnut" [data]="devicesData" [options]="donutOptions" height="144"></p-chart>
        </div>
      } @else {
        <div class="flex items-center justify-center h-24 text-sm text-gray-400">Aucune donnée</div>
      }
    </div>

    <!-- Sources -->
    <div class="bg-white rounded-xl ring-1 ring-gray-900/5 shadow-sm p-5 flex-1">
      <h3 class="text-sm font-semibold text-gray-900 mb-3">Sources de trafic</h3>
      @if (sources.length > 0) {
        <div class="space-y-2">
          @for (s of sources.slice(0, 6); track s.label) {
            <div class="flex items-center gap-2">
              <span class="text-sm shrink-0">{{ sourceIcon(s.label) }}</span>
              <span class="flex-1 text-xs text-gray-700 truncate">{{ s.label || '(Direct)' }}</span>
              <div class="w-16 bg-gray-100 rounded-full h-1.5 shrink-0">
                <div class="bg-indigo-400 h-1.5 rounded-full"
                  [style.width.%]="(s.visitors / (sources[0]?.visitors ?? 1)) * 100"></div>
              </div>
              <span class="text-xs font-semibold text-gray-900 w-8 text-right shrink-0">{{ formatNumber(s.visitors) }}</span>
            </div>
          }
        </div>
      } @else {
        <div class="flex items-center justify-center h-16 text-sm text-gray-400">Aucune donnée</div>
      }
    </div>

  </div>
</div>

<!-- ═══════════════════════════════════════════════════════════════════════
     Ligne 2 : Pages treeview (gauche) + Entrées & Sorties (droite)
══════════════════════════════════════════════════════════════════════════ -->
<div class="grid grid-cols-1 lg:grid-cols-5 gap-4">

  <!-- Pages treeview (toujours ouvert) -->
  <div class="lg:col-span-3 bg-white rounded-xl ring-1 ring-gray-900/5 shadow-sm overflow-hidden">

    <!-- Header + légende -->
    <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
      <h3 class="text-sm font-semibold text-gray-900">Pages</h3>
      <div class="flex items-center gap-3 text-xs text-gray-400">
        <span class="flex items-center gap-1">
          <span class="inline-block w-2 h-2 rounded-full bg-indigo-400"></span>
          Visiteurs
        </span>
        <span class="flex items-center gap-1">
          <span class="inline-block w-2 h-2 rounded-full bg-gray-300"></span>
          Vues
        </span>
      </div>
    </div>

    <!-- En-tête de colonnes -->
    <div class="flex items-center gap-2 px-4 py-1.5 border-b border-gray-50 bg-gray-50/60">
      <span class="flex-1 text-xs text-gray-400">Chemin</span>
      <div class="flex items-center gap-2 shrink-0">
        <span class="w-16 hidden sm:block"></span>
        <span class="text-xs text-indigo-400 w-8 text-right">Vis.</span>
        <span class="text-xs text-gray-400 w-8 text-right">Vues</span>
      </div>
    </div>

    @if (pageTree.length > 0) {
      <div class="overflow-y-auto" style="max-height:320px">
        <p-tree
          [value]="pageTree"
          styleClass="analytics-tree w-full"
          [style]="{'border':'none','padding':'4px 4px 4px 8px'}"
          [selectionMode]="null"
          (onNodeCollapse)="$event.node.expanded = true"
        >
          <ng-template pTemplate="default" let-node>
            <div class="flex items-center gap-2 w-full min-w-0 py-0.5">
              <span class="flex-1 text-xs text-gray-700 truncate font-mono" [title]="node.label">{{ node.label }}</span>
              <div class="flex items-center gap-2 shrink-0">
                <div class="w-16 bg-gray-100 rounded-full h-1 hidden sm:block">
                  <div class="bg-indigo-400 h-1 rounded-full transition-all duration-500"
                    [style.width.%]="pages[0] ? (node.data.visitors / pages[0].visitors) * 100 : 0"></div>
                </div>
                <span class="text-xs font-semibold text-indigo-600 w-8 text-right">{{ formatNumber(node.data.visitors) }}</span>
                <span class="text-xs text-gray-400 w-8 text-right">{{ node.data.pageviews > 0 ? formatNumber(node.data.pageviews) : '—' }}</span>
              </div>
            </div>
          </ng-template>
        </p-tree>
      </div>
    } @else {
      <p class="px-5 py-8 text-sm text-gray-400 text-center">Aucune donnée</p>
    }
  </div>

  <!-- Entrées & Sorties — graphe horizontal -->
  <div class="lg:col-span-2 bg-white rounded-xl ring-1 ring-gray-900/5 shadow-sm p-5">
    <h3 class="text-sm font-semibold text-gray-900 mb-4">Entrées &amp; Sorties</h3>
    @if (entryPages.length > 0 || exitPages.length > 0) {
      <div [style.height.px]="Math.max(entryPages.length, exitPages.length) * 34 + 56"
           style="min-height:180px; max-height:340px">
        <p-chart type="bar" [data]="entryExitData" [options]="entryExitOptions"></p-chart>
      </div>
    } @else {
      <div class="flex items-center justify-center h-32 text-sm text-gray-400">Aucune donnée</div>
    }
  </div>

</div>
